import ExcelJS from 'exceljs';
import { formatDateSimple, getHourAndMinutes } from 'utils/formatDate';
import { currencyFormatterForExcel } from 'utils/formatters';
import { getPhoneNumberWithoutAreaCode } from 'utils/getPhoneNumberWithoutAreaCode';
import { ContractingStateEnum } from 'v3/data/store/contracting/enums';
import { ContractingStateMapping } from 'v3/data/store/contracting/mapping';
import { ContractingType } from 'v3/data/store/contracting/types';
import { ContractingEventsEnum } from 'v3/data/store/seller/enum';
import * as api from 'v3/services/api';
import { calculateProductFinalPrice } from 'v3/services/utils/calculateFinalPrice';
import { FeatureFlagEnum } from 'v3/shared/enums/feature-flag.enum';

export const downloadOrdersExcelReport = async (list) => {
  const fetchSellerFeatureFlag = async () => {
    const response = await api.getFlagStatus(FeatureFlagEnum.IKB2C_4429_ENABLE_SELLER_FEATURE);
    return response?.data?.enabled;
  };
  const sellerFeature = await fetchSellerFeatureFlag();

  const file = await createExcel(list, sellerFeature);
  const url = window.URL.createObjectURL(file);
  const a = document.createElement('a');
  a.href = url;
  a.download = 'relatório-financiamento.xlsx';
  a.click();
};

const orderWorksheetFactory = (workbook: ExcelJS.Workbook, worksheetName: string, sellerFeature: boolean) => {
  const worksheet = workbook.addWorksheet(worksheetName);
  worksheet.columns = [
    { header: 'Cliente', key: 'name', width: 30 },
    { header: 'CPF', key: 'cpf', width: 15 },
    { header: 'Telefone', key: 'phoneNumber', width: 15 },
    { header: 'Email', key: 'email', width: 30 },
    { header: 'Valor do aparelho', key: 'value', width: 20 },
    { header: 'Aparelho', key: 'device', width: 20 },
    { header: 'Imei', key: 'imei', width: 20 },
    { header: 'Crédito aprovado', key: 'releasedAmount', width: 20 },
    { header: 'Valor financiado', key: 'requestedAmount', width: 20 },
    { header: 'Data', key: 'date', width: 25 },
    { header: 'Horário', key: 'time', width: 15 },
    { header: 'Data de Assinatura', key: 'contractSignDate', width: 20 },
    ...sellerFeature ? [{ header: 'Consultor', key: 'seller', width: 20 }] : [],
    { header: 'Status', key: 'status', width: 30 },
    { header: 'Loja', key: 'store', width: 20 },
    { header: 'CNPJ', key: 'cnpj', width: 20 },
    { header: 'Valor de entrada', key: 'inputValue', width: 20 },
    { header: 'Valor dos acessórios', key: 'accessoriesValue', width: 20 },
    { header: 'Seguro', key: 'insuranceValue', width: 20 },
    { header: 'Subtotal dos produtos', key: 'productSubtotal', width: 20 },
  ];

  worksheet.getRow(1).eachCell((cell) => {
    cell.font = { bold: true };
  });

  return worksheet;
};

const createExcel = async (orders: ContractingType[], sellerFeature: boolean) => {
  const workbook = new ExcelJS.Workbook();
  const worksheet = orderWorksheetFactory(workbook, 'Relatório', sellerFeature);

  orders.forEach((item) => {
    const { creditSimulation, person, createdAt, contractSignDate, ContractingEvents, status } = item;
    const contractingStatusTitle
      = ContractingStateMapping[status as ContractingStateEnum]?.title || 'Status desconhecido';

    const contractingSignatureEvent = ContractingEvents.find(
      (contractingEvent) => contractingEvent.event === ContractingEventsEnum.ContractSignature,
    );

    const contractSignatureSeller = contractingSignatureEvent ? contractingSignatureEvent?.seller.name : '-';
    const imei = creditSimulation.device?.imei ?? '-';
    const requestedAmount = creditSimulation.financing?.requestedAmount ?? '-';
    const releasedAmount = creditSimulation.financing?.releasedAmount ?? '-';

    const phoneNumber = person.phoneAreaCode ?
      person.phoneAreaCode + getPhoneNumberWithoutAreaCode(person.phoneNumber)
      : '-';

    const productName = creditSimulation.product ? creditSimulation.product.name : '-';
    let productSubtotal = 0;
    let productFinalPrice = 0;
    if (item.creditSimulation.product) {
      const accessoriesValue = item.creditSimulation.financing?.FinancingDetails?.accessoriesValue;
      const insuranceValue = item.creditSimulation.financing?.FinancingDetails?.insuranceValue;
      const discount = item.creditSimulation.product?.discount;
      const price = item.creditSimulation.product?.price;
      productFinalPrice = calculateProductFinalPrice(price, discount);
      productSubtotal = productFinalPrice + insuranceValue + accessoriesValue;
    }

    worksheet.addRow({
      name: person.fullName,
      cpf: person.cpf,
      phoneNumber,
      email: person.email ? person.email : '-',
      value: currencyFormatterForExcel(productFinalPrice),
      device: productName,
      imei,
      requestedAmount,
      releasedAmount,
      date: formatDateSimple(createdAt),
      time: getHourAndMinutes(createdAt),
      contractSignDate: contractSignDate ? formatDateSimple(contractSignDate) : 'Sem Assinatura',
      ...sellerFeature ? { seller: contractSignatureSeller } : {},
      status: contractingStatusTitle,
      store: creditSimulation.store.name,
      cnpj: creditSimulation.store.cnpj,
      inputValue: creditSimulation.financing?.inputValue ?
        currencyFormatterForExcel(creditSimulation.financing?.inputValue)
        : '-',
      accessoriesValue: creditSimulation.financing?.FinancingDetails?.accessoriesValue ?
        currencyFormatterForExcel(creditSimulation.financing?.FinancingDetails?.accessoriesValue)
        : '-',
      insuranceValue: creditSimulation.financing?.FinancingDetails?.insuranceValue ?
        currencyFormatterForExcel(creditSimulation.financing?.FinancingDetails?.insuranceValue)
        : '-',
      productSubtotal: productSubtotal ? currencyFormatterForExcel(productSubtotal) : '-',
    });
  });

  const buffer = await workbook.xlsx.writeBuffer();
  const fileType = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';
  const file = new Blob([buffer], { type: fileType });

  return file;
};
